import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Pager } from "@/components/Pager";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TABS = [["", "All"], ["submitted", "Submitted"], ["under_review", "Under Review"], ["approved", "Approved"], ["rejected", "Rejected"]];
const PAGE_SIZE = 20;

export default function Applications() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get("status") || "";
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });

  useEffect(() => { const t = setTimeout(() => { setQ(search); setPage(1); }, 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    api.get("/applications", { params: { status, search: q, page, page_size: PAGE_SIZE } }).then((r) => setData(r.data));
  }, [status, q, page]);

  return (
    <div data-testid="applications-page">
      <PageHeader eyebrow="Origination" title="Applications" subtitle={`${data.total} application${data.total === 1 ? "" : "s"}`} />
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="inline-flex flex-wrap rounded-md border bg-card p-1" data-testid="application-status-filter">
          {TABS.map(([v, l]) => (
            <button key={v || "all"} onClick={() => { const n = new URLSearchParams(params); v ? n.set("status", v) : n.delete("status"); setParams(n); setPage(1); }}
              data-testid={`app-filter-${v || "all"}`}
              className={`rounded px-3.5 py-1.5 text-sm font-medium transition-colors ${status === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="h-10 bg-card pl-9" data-testid="application-search-input" />
        </div>
      </div>
      <div className="fade-up overflow-x-auto rounded-lg border bg-card">
        <Table data-testid="applications-table">
          <TableHeader><TableRow className="hover:bg-transparent">
            {["Applicant", "Amount", "Term", "Purpose", "Submitted", "Status"].map((h) => <TableHead key={h} className="eyebrow h-11 whitespace-nowrap">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((a) => (
              <TableRow key={a.id} className="cursor-pointer" onClick={() => navigate(`/admin/applications/${a.id}`)} data-testid={`application-row-${a.applicant_email}`}>
                <TableCell><div className="font-medium">{a.applicant_name}</div><div className="text-xs text-muted-foreground">{a.applicant_email}</div></TableCell>
                <TableCell className="num font-medium">{money(a.amount_cents)}</TableCell>
                <TableCell className="num">{a.term_months} mo</TableCell>
                <TableCell className="max-w-[220px] truncate text-sm">{a.purpose}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{fmtDate(a.submitted_at)}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell>
              </TableRow>
            ))}
            {!data.items.length && <TableRow><TableCell colSpan={6} className="py-14 text-center text-sm text-muted-foreground" data-testid="applications-empty">No applications.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} testid="applications-pager" />
    </div>
  );
}
